import * as React from 'react';

import {
  AccountType,
  enumAccountType,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter, cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import * as accountListState from '../../_state/accountList.state';
import { useTranslations } from '../../i18n/useTranslations';

interface AccountTypeOption {
  id: AccountType;
  label: string;
}

interface AccountTypeFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ ACCOUNT TYPE FILTER ⚛️ ⚛️ ⚛️
//
export const AccountTypeFilter: React.FC<AccountTypeFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedTypesSet, setSelectedTypesSet] = useAtom(
    accountListState.$accountTypes,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const selectedTypes = Array.from(selectedTypesSet);

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedTypes),
    [selectedTypes],
  );

  const typeLabels: Record<AccountType, string> = React.useMemo(
    () => ({
      [enumAccountType.Commercial]: t.accountsView.form.type.commercial,
      [enumAccountType.Individual]: t.accountsView.form.type.individual,
    }),
    [t],
  );

  const typeOptions: AccountTypeOption[] = React.useMemo(
    () =>
      Object.values(enumAccountType).map((type) => ({
        id: type,
        label: typeLabels[type],
      })),
    [typeLabels],
  );

  const filteredOptions = React.useMemo(
    () =>
      typeOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [typeOptions, searchQuery],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedTypesSet(new Set(Array.from(keys) as AccountType[]));
    },
    [setSelectedTypesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypesSet(new Set());
  }, [setSelectedTypesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: AccountTypeOption, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.label}
        </div>
      </div>
    ),
    [],
  );

  const selectedTypeOptions = React.useMemo(
    () => typeOptions.filter((option) => selectedTypes.includes(option.id)),
    [typeOptions, selectedTypes],
  );

  return (
    <MultiSelectFilter
      className="@AccountTypeFilter min-w-[200px]"
      entries={filteredOptions}
      selectedEntries={selectedTypeOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(type) => type.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.accountsView.toolbar.filterType}
      isLoading={false}
      isFetching={false}
    />
  );
};
